import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Camera, Package, Truck, CheckCircle, 
  RefreshCw, ArrowLeft, MapPin, Clock,
  FileText, RotateCcw, Send
} from 'lucide-react';

const API_URL = getApiBaseUrl();
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

export default function AdminInspectionWorkflow() {
  const [pendingFulfillment, setPendingFulfillment] = useState([]);
  const [awaitingReturn, setAwaitingReturn] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Shipping form
  const [shippingForm, setShippingForm] = useState({
    kit_order_id: '',
    tracking_number: '',
    carrier: 'Royal Mail'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${getToken()}` };
      
      const [pendingRes, awaitingRes, casesRes] = await Promise.all([
        fetch(`${API_URL}/api/inspection-workflow/admin/pending-fulfillment`, { headers }),
        fetch(`${API_URL}/api/inspection-workflow/admin/awaiting-return`, { headers }),
        fetch(`${API_URL}/api/inspection-workflow/cases`, { headers })
      ]);
      
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingFulfillment(data.pending_orders || []);
      }
      if (awaitingRes.ok) {
        const data = await awaitingRes.json();
        setAwaitingReturn(data.awaiting_return || []);
      }
      if (casesRes.ok) {
        const data = await casesRes.json();
        setAllCases(data.cases || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const markShipped = async (kitOrderId) => {
    if (!shippingForm.tracking_number) {
      toast.error('Please enter tracking number');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/inspection-workflow/admin/kit-orders/${kitOrderId}/ship`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tracking_number: shippingForm.tracking_number,
          carrier: shippingForm.carrier
        })
      });
      
      if (response.ok) {
        toast.success('Kit marked as shipped');
        setShippingForm({ kit_order_id: '', tracking_number: '', carrier: 'Royal Mail' });
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to mark as shipped');
    }
  };

  const markDelivered = async (kitOrderId) => {
    try {
      const response = await fetch(`${API_URL}/api/inspection-workflow/admin/kit-orders/${kitOrderId}/delivered`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        toast.success('Kit marked as delivered');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const markReturned = async (kitOrderId) => {
    try {
      const response = await fetch(`${API_URL}/api/inspection-workflow/admin/kit-orders/${kitOrderId}/returned`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        toast.success('Kit marked as returned');
        loadData();
      }
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'report_ready': return 'bg-green-100 text-green-700';
      case 'kit_delivered': case 'kit_returned': return 'bg-blue-100 text-blue-700';
      case 'kit_shipped': return 'bg-purple-100 text-purple-700';
      case 'kit_ordered': case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link to="/admin/console">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin Console
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Camera className="w-8 h-8 text-orange-500" />
              Inspection Workflow
            </h1>
            <p className="text-gray-600">Manage LiDAR kit logistics and inspections</p>
          </div>
        </div>
        <Button onClick={loadData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <Card className="p-4 border-yellow-200 bg-yellow-50">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-5 h-5 text-yellow-600" />
            <span className="text-sm text-yellow-600">Pending Fulfillment</span>
          </div>
          <div className="text-2xl font-bold text-yellow-700">{pendingFulfillment.length}</div>
        </Card>
        <Card className="p-4 border-purple-200 bg-purple-50">
          <div className="flex items-center gap-2 mb-1">
            <Truck className="w-5 h-5 text-purple-600" />
            <span className="text-sm text-purple-600">In Transit</span>
          </div>
          <div className="text-2xl font-bold text-purple-700">
            {allCases.filter(c => c.status === 'kit_shipped').length}
          </div>
        </Card>
        <Card className="p-4 border-blue-200 bg-blue-50">
          <div className="flex items-center gap-2 mb-1">
            <RotateCcw className="w-5 h-5 text-blue-600" />
            <span className="text-sm text-blue-600">Awaiting Return</span>
          </div>
          <div className="text-2xl font-bold text-blue-700">{awaitingReturn.length}</div>
        </Card>
        <Card className="p-4 border-green-200 bg-green-50">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-green-600" />
            <span className="text-sm text-green-600">Reports Ready</span>
          </div>
          <div className="text-2xl font-bold text-green-700">
            {allCases.filter(c => c.status === 'report_ready').length}
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {['pending', 'awaiting', 'all'].map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'default' : 'outline'}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'pending' && 'Pending Fulfillment'}
            {tab === 'awaiting' && 'Awaiting Return'}
            {tab === 'all' && 'All Cases'}
          </Button>
        ))}
      </div>

      {/* Pending Fulfillment */}
      {activeTab === 'pending' && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-yellow-50">
            <h2 className="font-semibold flex items-center gap-2">
              <Package className="w-5 h-5 text-yellow-600" />
              Kits Pending Shipment
            </h2>
          </div>
          
          {pendingFulfillment.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No kits pending fulfillment</p>
            </div>
          ) : (
            <div className="divide-y">
              {pendingFulfillment.map((order) => (
                <div key={order.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">Order: {order.order_ref}</div>
                      {order.property_address && (
                        <div className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {order.property_address.line1}, {order.property_address.city}, {order.property_address.postcode}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        Created: {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {shippingForm.kit_order_id === order.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Tracking number"
                            value={shippingForm.tracking_number}
                            onChange={(e) => setShippingForm({...shippingForm, tracking_number: e.target.value})}
                            className="px-3 py-1 border rounded text-sm w-40"
                          />
                          <select
                            value={shippingForm.carrier}
                            onChange={(e) => setShippingForm({...shippingForm, carrier: e.target.value})}
                            className="px-2 py-1 border rounded text-sm"
                          >
                            <option>Royal Mail</option>
                            <option>DPD</option>
                            <option>Hermes</option>
                            <option>UPS</option>
                          </select>
                          <Button size="sm" onClick={() => markShipped(order.id)}>
                            <Send className="w-4 h-4 mr-1" />
                            Ship
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => setShippingForm({kit_order_id: '', tracking_number: '', carrier: 'Royal Mail'})}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button 
                          size="sm" 
                          onClick={() => setShippingForm({...shippingForm, kit_order_id: order.id})}
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Mark Shipped
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Awaiting Return */}
      {activeTab === 'awaiting' && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-blue-50">
            <h2 className="font-semibold flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-600" />
              Kits Awaiting Return
            </h2>
          </div>
          
          {awaitingReturn.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <RotateCcw className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No kits awaiting return</p>
            </div>
          ) : (
            <div className="divide-y">
              {awaitingReturn.map((order) => (
                <div key={order.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">Order: {order.order_ref}</div>
                      <div className="text-sm text-gray-500">
                        Status: {order.status}
                        {order.tracking_number && ` • Tracking: ${order.tracking_number}`}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {order.status === 'delivered' && (
                        <Button size="sm" variant="outline" onClick={() => markDelivered(order.id)}>
                          Confirm Delivered
                        </Button>
                      )}
                      <Button size="sm" onClick={() => markReturned(order.id)}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Mark Returned
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* All Cases */}
      {activeTab === 'all' && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold flex items-center gap-2">
              <Camera className="w-5 h-5 text-gray-600" />
              All Inspection Cases
            </h2>
          </div>
          
          {allCases.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Camera className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No inspection cases yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {allCases.map((inspCase) => (
                <div key={inspCase.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {inspCase.inspection_type?.replace('_', ' ').toUpperCase() || 'Inspection'}
                      </div>
                      {inspCase.property_address && (
                        <div className="text-sm text-gray-500">
                          {inspCase.property_address.line1}, {inspCase.property_address.city}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        Created: {new Date(inspCase.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inspCase.status)}`}>
                      {inspCase.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Workflow Info */}
      <Card className="mt-6 p-6 bg-orange-50 border-orange-200">
        <h3 className="font-semibold text-orange-800 mb-3 flex items-center gap-2">
          <Camera className="w-5 h-5" />
          Inspection Kit Workflow
        </h3>
        <div className="flex items-center justify-between text-sm text-orange-700">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-yellow-200 flex items-center justify-center">1</span>
            <span>Kit Ordered</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center">2</span>
            <span>Shipped</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center">3</span>
            <span>Delivered</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center">4</span>
            <span>Returned</span>
          </div>
          <span>→</span>
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center">5</span>
            <span>Report Ready</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
